import React, { useState, useEffect } from 'react';
import { Search, Plus, Trash2, Loader2, TrendingUp, Users, BarChart3, RefreshCw, BadgeCheck, Target, Lock } from 'lucide-react';
import { translations } from '../../translations';
import { supabase } from '../../lib/supabase';
import { hikerApi } from '../../services/hikerApi';
import { imageService } from '../../services/imageService';
import { products } from '../../stripe-config';
import { createCheckoutSession } from '../../lib/stripe';
import { CompetitorChart } from '../../components/Competitors/CompetitorChart';
import { AddCompetitorModal } from '../../components/Competitors/AddCompetitorModal';

interface CompetitorsProps {
  isDarkMode: boolean;
  language: string;
}

interface Competitor {
  id: string;
  name: string;
  username: string;
  avatar: string;
  followers: string;
  following: string;
  posts: number;
  is_verified: boolean;
  is_private: boolean;
  biography: string;
  follower_count: number;
  following_count: number;
  media_count: number;
  follower_history: Array<{
    date: string;
    count: number;
    gains: number;
  }>;
  created_at: string;
  updated_at: string;
  subscription_active: boolean;
  subscription_active: 'inactive' | 'active' | 'FREE';
}

export function Competitors({ isDarkMode, language }: CompetitorsProps) {
  const [competitors, setCompetitors] = useState<Competitor[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCompetitor, setSelectedCompetitor] = useState<Competitor | null>(null);
  const [isUpdating, setIsUpdating] = useState<Record<string, boolean>>({});
  const [isAnnual, setIsAnnual] = useState(true);
  const [selectedTab, setSelectedTab] = useState(() => {
    return localStorage.getItem('competitorsSelectedTab') || 'overview';
  });
  const [localSubscriptionStates, setLocalSubscriptionStates] = useState<Record<string, 'inactive' | 'active' | 'FREE'>>({});
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const t = translations[language as keyof typeof translations];

  // Force refresh competitor data every 2 seconds when selected
  useEffect(() => {
    if (!selectedCompetitor) return;

    const interval = setInterval(async () => {
      try {
        const { data: updatedCompetitor, error } = await supabase
          .from('competitors')
          .select('subscription_active')
          .eq('id', selectedCompetitor.id)
          .single();

        if (!error && updatedCompetitor) {
          const newSubscriptionState = updatedCompetitor.subscription_active;
          
          // Update local state if different
          setLocalSubscriptionStates(prev => {
            if (prev[selectedCompetitor.id] !== newSubscriptionState) {
              console.log(`🔄 Competitor ${selectedCompetitor.id} subscription changed to:`, newSubscriptionState);
              return {
                ...prev,
                [selectedCompetitor.id]: newSubscriptionState
              };
            }
            return prev;
          });

          // Update competitors list
          setCompetitors(prev => 
            prev.map(comp => 
              comp.id === selectedCompetitor.id 
                ? { ...comp, subscription_active: newSubscriptionState }
                : comp
            )
          );

          // Update selected competitor
          setSelectedCompetitor(prev => 
            prev ? { ...prev, subscription_active: newSubscriptionState } : prev
          );
        }
      } catch (error) {
        console.error('Error polling competitor subscription:', error);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(interval);
  }, [selectedCompetitor?.id]);

  const handleSelectTab = (tab: string) => {
    setSelectedTab(tab);
    localStorage.setItem('competitorsSelectedTab', tab);
  };

  useEffect(() => {
    loadCompetitors();
  }, []);

  const loadCompetitors = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('competitors')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setCompetitors(data || []);
      
      // Initialize local subscription states
      const subscriptionStates: Record<string, 'inactive' | 'active' | 'FREE'> = {};
      data?.forEach(competitor => {
        subscriptionStates[competitor.id] = competitor.subscription_active;
      });
      setLocalSubscriptionStates(subscriptionStates);
      
      if (data && data.length > 0 && !selectedCompetitor) {
        setSelectedCompetitor(data[0]);
      }
    } catch (err) {
      console.error('Error loading competitors:', err);
      setError('Failed to load competitors');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddCompetitor = async (username: string, displayName: string) => {
    try {
      const cleanUsername = username.replace('@', '').toLowerCase().trim();
      
      // Check if competitor already exists
      const { data: existing } = await supabase
        .from('competitors')
        .select('id')
        .eq('username', '@' + cleanUsername)
        .single();

      if (existing) {
        throw new Error('This competitor is already being tracked');
      }

      // Get account info from Hiker API
      const result = await hikerApi.getAccountInfo(cleanUsername);
      
      if (result.error || !result.data) {
        throw new Error(result.message || 'Failed to fetch account information');
      }

      // Upload avatar
      let avatarUrl = result.data.profilePicUrl || '';
      if (avatarUrl) {
        avatarUrl = await imageService.uploadAvatar(avatarUrl, cleanUsername);
      }

      const followerCount = parseInt(result.data.followers || '0', 10);

      // Create competitor record
      const { data: userData } = await supabase.auth.getUser();
      const competitorData = {
        name: displayName || result.data.fullName || cleanUsername,
        username: '@' + cleanUsername,
        avatar: avatarUrl,
        followers: result.data.followers || '0',
        following: result.data.following || '0',
        posts: result.data.posts || 0,
        is_verified: result.data.isVerified || false,
        is_private: result.data.isPrivate || false,
        biography: result.data.biography || '',
        follower_count: followerCount,
        following_count: parseInt(result.data.following || '0', 10),
        media_count: result.data.posts || 0,
        follower_history: [{
          date: new Date().toISOString(),
          count: followerCount,
          gains: 0
        }],
        created_by: userData.user?.id,
        subscription_active: 'inactive'
      };

      const { data, error } = await supabase
        .from('competitors')
        .insert(competitorData)
        .select()
        .single();

      if (error) throw error;

      await loadCompetitors();
      setShowAddModal(false);
    } catch (err) {
      console.error('Error adding competitor:', err);
      throw err;
    }
  };

  const handleUpdateCompetitor = async (competitorId: string) => {
    try {
      setIsUpdating(prev => ({ ...prev, [competitorId]: true }));
      
      const competitor = competitors.find(c => c.id === competitorId);
      if (!competitor) return;

      const cleanUsername = competitor.username.replace('@', '');
      const result = await hikerApi.getAccountInfo(cleanUsername);
      
      if (result.error || !result.data) {
        throw new Error(result.message || 'Failed to fetch account information');
      }

      const newFollowerCount = parseInt(result.data.followers || '0', 10);
      const oldFollowerCount = competitor.follower_count;
      const gains = newFollowerCount - oldFollowerCount;

      // Update follower history
      const updatedHistory = [
        ...competitor.follower_history,
        {
          date: new Date().toISOString(),
          count: newFollowerCount,
          gains: gains
        }
      ];

      // Update competitor
      const { error } = await supabase
        .from('competitors')
        .update({
          followers: result.data.followers || '0',
          following: result.data.following || '0',
          posts: result.data.posts || 0,
          is_verified: result.data.isVerified || false,
          is_private: result.data.isPrivate || false,
          biography: result.data.biography || '',
          follower_count: newFollowerCount,
          following_count: parseInt(result.data.following || '0', 10),
          media_count: result.data.posts || 0,
          follower_history: updatedHistory,
          updated_at: new Date().toISOString()
        })
        .eq('id', competitorId);

      if (error) throw error;

      await loadCompetitors();
    } catch (err) {
      console.error('Error updating competitor:', err);
      setError('Failed to update competitor data');
    } finally {
      setIsUpdating(prev => ({ ...prev, [competitorId]: false }));
    }
  };

  const handleDeleteCompetitor = async (competitorId: string) => {
    try {
      const { error } = await supabase
        .from('competitors')
        .delete()
        .eq('id', competitorId);

      if (error) throw error;

      await loadCompetitors();
      
      if (selectedCompetitor?.id === competitorId) {
        setSelectedCompetitor(competitors.find(c => c.id !== competitorId) || null);
      }
    } catch (err) {
      console.error('Error deleting competitor:', err);
      setError('Failed to delete competitor');
    }
  };

  const filteredCompetitors = competitors.filter(competitor =>
    competitor.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    competitor.username.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatNumber = (num: number | string) => {
    const value = typeof num === 'string' ? parseInt(num.replace(/[^0-9]/g, ''), 10) : num;
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const calculateGrowthRate = (competitor: Competitor) => {
    if (competitor.follower_history.length < 2) return 0;
    
    const firstEntry = competitor.follower_history[0];
    const lastEntry = competitor.follower_history[competitor.follower_history.length - 1];
    
    return lastEntry.count - firstEntry.count;
  };

  // Check if the selected competitor has an active subscription
  const subscriptionStatus = selectedCompetitor 
    ? (localSubscriptionStates[selectedCompetitor.id] ?? selectedCompetitor.subscription_active)
    : 'inactive';
  
  const hasActiveSubscription = subscriptionStatus === 'active' || subscriptionStatus === 'FREE';
  const isFreeSubscription = subscriptionStatus === 'FREE';

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Competitors List Sidebar */}
      <div className={`w-72 border-r h-screen overflow-hidden flex flex-col ${
        isDarkMode 
          ? 'bg-[#121212] border-[#2b2b2b]' 
          : 'bg-white border-[#e5e7eb]'
      }`}>
        <div className="p-3 space-y-3">
          <div className="flex items-center gap-2">
            <Target size={20} className={isDarkMode ? 'text-gray-200' : 'text-gray-900'} strokeWidth={2} />
            <h1 className={`text-lg font-semibold ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              Competitors
            </h1>
          </div>
          
          <button
            onClick={() => setShowAddModal(true)}
            className="w-full bg-orange-500 text-white rounded-lg py-2 flex items-center justify-center gap-1.5 hover:bg-orange-600 transition-colors text-[13px] font-medium"
          >
            <Plus size={15} strokeWidth={2.5} />
            <span>Add Competitor</span>
          </button>
          
          <div className="relative">
            <input
              type="text"
              placeholder="Search competitors..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full rounded-lg pl-9 pr-4 py-2 text-[13px] placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                isDarkMode 
                  ? 'bg-[#2b2b2b] text-gray-200' 
                  : 'bg-gray-50 text-gray-900'
              }`}
            />
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={2.3} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="mx-3 mb-3 p-3 rounded-lg bg-red-50 text-red-500 text-sm">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-gray-400" />
            </div>
          ) : filteredCompetitors.length > 0 ? (
            <div className="px-3 space-y-2 pb-4">
              {filteredCompetitors.map(competitor => (
                <button
                  key={competitor.id}
                  onClick={() => setSelectedCompetitor(competitor)}
                  className={`w-full p-2 rounded-lg transition-colors flex items-center gap-3 group ${
                    selectedCompetitor?.id === competitor.id 
                      ? isDarkMode ? 'bg-[#1c1c1c]' : 'bg-gray-50'
                      : ''
                  } ${
                    isDarkMode 
                      ? 'hover:bg-[#1c1c1c]' 
                      : 'hover:bg-gray-50'
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
                    <img
                      src={competitor.avatar || 'https://via.placeholder.com/150'}
                      alt={competitor.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      crossOrigin="anonymous"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <div className={`text-[13px] font-medium ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>
                        {competitor.name}
                      </div>
                      {competitor.is_private && (
                        <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                          isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                        }`}>
                          Private
                        </span>
                      )}
                    </div>
                    <div className={`text-[12px] ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    } text-left`}>
                      {competitor.username}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-12">
              <p className={`text-sm ${
                isDarkMode ? 'text-gray-400' : 'text-gray-500'
              }`}>
                No competitors found
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-8">
        {selectedCompetitor ? (
          <div className="space-y-6">
            {/* Competitor Header */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <img
                  src={selectedCompetitor.avatar || 'https://via.placeholder.com/150'}
                  alt={selectedCompetitor.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className={`font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                      {selectedCompetitor.name}
                    </h2>
                    {selectedCompetitor.is_verified && (
                      <BadgeCheck 
                        size={16} 
                        className="text-[#0095F6]" 
                        strokeWidth={2.5} 
                      />
                    )}
                    {selectedCompetitor.is_private && (
                      <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${
                        isDarkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'
                      }`}>
                        Private
                      </span>
                    )}
                  </div>
                  <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {selectedCompetitor.username}
                  </p>
                  {selectedCompetitor.biography && (
                    <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {selectedCompetitor.biography.split('\n').map((line, index) => (
                        <span key={index}>
                          {line}
                          {index < selectedCompetitor.biography.split('\n').length - 1 && <br />}
                        </span>
                      ))}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs Navigation */}
            <div className="border-b border-gray-200 dark:border-gray-800 overflow-x-auto">
              <nav className="flex gap-6 whitespace-nowrap">
                <button
                  onClick={() => hasActiveSubscription && handleSelectTab('overview')}
                  className={`pb-4 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    !hasActiveSubscription ? 'opacity-50 cursor-not-allowed' : ''
                  } ${
                    selectedTab === 'overview'
                      ? 'border-orange-500 text-orange-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  {!hasActiveSubscription && <Lock size={12} />}
                  Vue d'ensemble
                </button>
                <button
                  onClick={() => handleSelectTab('plans')}
                  className={`pb-4 text-sm font-medium border-b-2 transition-colors ${
                    selectedTab === 'plans'
                      ? 'border-orange-500 text-orange-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
                >
                  Plans
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="pt-4">
              {selectedTab === 'overview' && hasActiveSubscription && (
                <div className="space-y-6">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className={`p-4 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                        : 'bg-white border-[#e5e7eb]'
                    }`}>
                      <div className={`text-[13px] mb-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>Followers</div>
                      <div className={`text-xl font-semibold ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>{formatNumber(selectedCompetitor.follower_count)}</div>
                    </div>
                    <div className={`p-4 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                        : 'bg-white border-[#e5e7eb]'
                    }`}>
                      <div className={`text-[13px] mb-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>Growth Rate</div>
                      <div className={`text-xl font-semibold ${
                        calculateGrowthRate(selectedCompetitor) >= 0 
                          ? 'text-emerald-500' 
                          : 'text-red-500'
                      }`}>
                        {calculateGrowthRate(selectedCompetitor) >= 0 ? '+' : ''}
                        {Math.abs(calculateGrowthRate(selectedCompetitor)).toLocaleString()}
                      </div>
                    </div>
                    <div className={`p-4 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                        : 'bg-white border-[#e5e7eb]'
                    }`}>
                      <div className={`text-[13px] mb-1 ${
                        isDarkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>Posts</div>
                      <div className={`text-xl font-semibold ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>{formatNumber(selectedCompetitor.media_count)}</div>
                    </div>
                  </div>

                  {/* Follower Evolution Chart */}
                  <div className={`p-6 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                      : 'bg-white border-[#e5e7eb]'
                  }`}>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className={`text-lg font-semibold ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>Follower Evolution</h2>
                      <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${
                        calculateGrowthRate(selectedCompetitor) >= 0
                          ? 'text-emerald-600 bg-emerald-50' 
                          : 'text-red-600 bg-red-50'
                      }`}>
                        <TrendingUp size={14} strokeWidth={2.5} />
                        <span className="text-[13px] font-medium">
                          {calculateGrowthRate(selectedCompetitor) >= 0 ? '+' : ''}
                          {Math.abs(calculateGrowthRate(selectedCompetitor)).toLocaleString()}
                        </span>
                      </div>
                    </div>
                    
                    {selectedCompetitor.follower_history.length > 1 ? (
                      <CompetitorChart
                        data={selectedCompetitor.follower_history}
                        isDarkMode={isDarkMode}
                        language={language}
                      />
                    ) : (
                      <div className="h-[300px] flex items-center justify-center">
                        <p className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Not enough data to display chart
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Daily Gains Chart */}
                  <div className={`p-6 rounded-lg border ${
                    isDarkMode 
                      ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                      : 'bg-white border-[#e5e7eb]'
                  }`}>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className={`text-lg font-semibold ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>Daily Gains</h2>
                    </div>
                    
                    {selectedCompetitor.follower_history.length > 1 ? (
                      <div className="flex">
                        <div className="pr-4 flex flex-col justify-between text-[10px]">
                          {[-200, -100, 0, 100, 200].reverse().map((value, index) => (
                            <div
                              key={index}
                              className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}
                            >
                              {value >= 0 ? '+' : ''}{value}
                            </div>
                          ))}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="relative h-[200px]">
                            <div className="absolute inset-0">
                              {/* Grid lines */}
                              {Array.from({ length: 5 }).map((_, i) => (
                                <div
                                  key={`h-${i}`}
                                  className={`absolute w-full border-t ${
                                    isDarkMode ? 'border-gray-800' : 'border-gray-100'
                                  }`}
                                  style={{ top: `${(i * 25)}%` }}
                                />
                              ))}
                              
                              {/* Zero line - make it more prominent */}
                              <div 
                                className={`absolute w-full border-t-2 ${
                                  isDarkMode ? 'border-gray-600' : 'border-gray-300'
                                }`}
                                style={{ top: '50%' }}
                              />
                            </div>

                            <svg
                              className="absolute inset-0 w-full h-full overflow-visible"
                              preserveAspectRatio="none"
                              onMouseMove={(e) => {
                                const svg = e.currentTarget;
                                const rect = svg.getBoundingClientRect();
                                const x = e.clientX - rect.left;
                                
                                const barWidth = rect.width / (selectedCompetitor.follower_history.length - 1);
                                const index = Math.floor(x / barWidth) + 1; // +1 because we skip first entry
                                
                                if (index >= 1 && index < selectedCompetitor.follower_history.length) {
                                  const entry = selectedCompetitor.follower_history[index];
                                  const previousEntry = selectedCompetitor.follower_history[index - 1];
                                  const dailyGain = entry.count - previousEntry.count;
                                  
                                  const formatDate = (dateStr: string) => {
                                    const date = new Date(dateStr);
                                    return new Intl.DateTimeFormat(language === 'FR' ? 'fr-FR' : 'en-US', {
                                      day: '2-digit',
                                      month: '2-digit'
                                    }).format(date);
                                  };
                                  
                                  const formatNumber = (num: number) => {
                                    const prefix = num >= 0 ? '+' : '';
                                    return prefix + num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
                                  };
                                  
                                  // Create tooltip
                                  const tooltip = document.getElementById('daily-gains-tooltip');
                                  if (tooltip) {
                                    tooltip.textContent = `${formatDate(entry.date)}: ${formatNumber(dailyGain)}`;
                                    tooltip.style.left = `${e.clientX + 10}px`;
                                    tooltip.style.top = `${e.clientY - 30}px`;
                                    tooltip.style.display = 'block';
                                  }
                                }
                              }}
                              onMouseLeave={() => {
                                const tooltip = document.getElementById('daily-gains-tooltip');
                                if (tooltip) {
                                  tooltip.style.display = 'none';
                                }
                              }}
                            >
                              {selectedCompetitor.follower_history.map((entry, i) => {
                                if (i === 0) return null; // Skip first entry as it has no previous data
                                
                                const previousEntry = selectedCompetitor.follower_history[i - 1];
                                const dailyGain = entry.count - previousEntry.count;
                                
                                const barWidth = 100 / (selectedCompetitor.follower_history.length - 1);
                                const x = (i - 1) * barWidth;
                                
                                // Calculate height and position relative to zero line (50%)
                                const maxGain = 200; // Max expected gain for scaling
                                const heightPercent = Math.min(Math.abs(dailyGain) / maxGain * 25, 25); // Max 25% of chart height
                                const y = dailyGain >= 0 
                                  ? 50 - heightPercent 
                                  : 50;

                                return (
                                  <rect
                                    key={i}
                                    x={`${x}%`}
                                    y={`${y}%`}
                                    width={`${barWidth * 0.8}%`}
                                    height={`${heightPercent}%`}
                                    fill={dailyGain >= 0 ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'}
                                    rx="2"
                                    transform={`translate(${barWidth * 0.1}%, 0)`}
                                    className="transition-all duration-200 hover:opacity-80"
                                  />
                                );
                              })}
                            </svg>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center">
                        <p className={`text-sm ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          Not enough data to display chart
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Activity Timeline */}
                  {selectedCompetitor && (
                    <div className={`p-6 rounded-lg border ${
                      isDarkMode 
                        ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                        : 'bg-white border-[#e5e7eb]'
                    }`}>
                      <h2 className={`text-lg font-semibold mb-6 ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-900'
                      }`}>Activity Timeline</h2>
                      
                      <div className="space-y-4 max-h-96 overflow-y-auto">
                        {/* Generate mock activity timeline based on follower history */}
                        {selectedCompetitor.follower_history && selectedCompetitor.follower_history.length > 0 ? (
                          selectedCompetitor.follower_history
                            .slice()
                            .reverse()
                            .slice(0, 10)
                            .map((entry, index) => {
                              const date = new Date(entry.date);
                              const timeAgo = Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
                              const gains = entry.gains || 0;
                              
                              // Generate different types of activities
                              const activities = [];
                              
                              // Follower change activity
                              if (gains !== 0) {
                                activities.push({
                                  type: gains > 0 ? 'follower_gain' : 'follower_loss',
                                  icon: gains > 0 ? '📈' : '📉',
                                  text: gains > 0 
                                    ? `a gagné ${Math.abs(gains).toLocaleString()} abonnés`
                                    : `a perdu ${Math.abs(gains).toLocaleString()} abonnés`,
                                  color: gains > 0 ? 'text-emerald-500' : 'text-red-500'
                                });
                              }
                              
                              // Random additional activities for variety
                              if (index % 3 === 0 && index > 0) {
                                activities.push({
                                  type: 'post',
                                  icon: '📸',
                                  text: 'a publié un nouveau post',
                                  color: 'text-blue-500'
                                });
                              }
                              
                              if (index % 5 === 0 && index > 0) {
                                activities.push({
                                  type: 'bio_change',
                                  icon: '✏️',
                                  text: 'a modifié sa biographie',
                                  color: 'text-purple-500'
                                });
                              }
                              
                              if (index % 7 === 0 && index > 0) {
                                activities.push({
                                  type: 'story',
                                  icon: '📱',
                                  text: 'a publié une story',
                                  color: 'text-orange-500'
                                });
                              }
                              
                              return activities.map((activity, actIndex) => (
                                <div key={`${index}-${actIndex}`} className="flex items-start gap-3">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                                    isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
                                  }`}>
                                    {activity.icon}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className={`text-sm ${
                                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                                    }`}>
                                      <span className="font-medium">{selectedCompetitor.username}</span>
                                      <span className="mx-1">{activity.text}</span>
                                    </div>
                                    <div className={`text-xs mt-1 ${
                                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                                    }`}>
                                      {timeAgo === 0 ? "Aujourd'hui" : 
                                       timeAgo === 1 ? "Hier" : 
                                       `Il y a ${timeAgo} jours`}
                                    </div>
                                  </div>
                                  {activity.type === 'follower_gain' || activity.type === 'follower_loss' ? (
                                    <div className={`text-sm font-medium ${activity.color}`}>
                                      {gains > 0 ? '+' : ''}{gains.toLocaleString()}
                                    </div>
                                  ) : null}
                                </div>
                              ));
                            })
                            .flat()
                        ) : (
                          <div className="text-center py-8">
                            <p className={`text-sm ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}>
                              Aucune activité récente
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Tooltip */}
                  <div
                    id="daily-gains-tooltip"
                    className={`fixed z-50 px-2 py-1 text-xs rounded shadow-lg pointer-events-none ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border border-[#3b3b3b]'
                        : 'bg-white text-gray-900 border border-gray-200'
                    }`}
                    style={{ display: 'none' }}
                  />
                </div>
              )}
              
              {selectedTab === 'overview' && !hasActiveSubscription && (
                <div className={`p-8 rounded-lg border text-center ${
                  isDarkMode 
                    ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                    : 'bg-white border-[#e5e7eb]'
                }`}>
                  <Lock size={48} className={`mx-auto mb-4 ${
                    isDarkMode ? 'text-gray-600' : 'text-gray-400'
                  }`} />
                  <h3 className={`text-lg font-semibold mb-2 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-900'
                  }`}>
                    Fonctionnalité Premium
                  </h3>
                  <p className={`text-sm mb-6 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Pour accéder aux statistiques détaillées des competitors, vous devez souscrire au plan Amplr Track Competitor.
                  </p>
                  <button
                    onClick={() => handleSelectTab('plans')}
                    className="px-6 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
                  >
                    Voir les plans
                  </button>
                </div>
              )}
              
              {selectedTab === 'plans' && !isFreeSubscription && (
                <div className={`p-6 rounded-lg border ${
                  isDarkMode 
                    ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                    : 'bg-white border-[#e5e7eb]'
                }`}>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className={`text-lg font-semibold ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-900'
                    }`}>Track Competitor</h2>
                    
                    <div className={`inline-flex rounded-lg p-0.5 ${
                      isDarkMode 
                        ? 'bg-[#2b2b2b]' 
                        : 'bg-gray-100 border border-gray-200'
                    }`}>
                      <button
                        onClick={() => setIsAnnual(false)}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          !isAnnual
                            ? isDarkMode
                              ? 'bg-[#1c1c1c] text-white'
                              : 'bg-white text-gray-900 shadow-sm border border-gray-200'
                            : isDarkMode
                              ? 'text-gray-400'
                              : 'text-gray-500'
                        }`}
                      >
                        Mensuel
                      </button>
                      <button
                        onClick={() => setIsAnnual(true)}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          isAnnual
                            ? isDarkMode
                              ? 'bg-[#1c1c1c] text-white'
                              : 'bg-white text-gray-900 shadow-sm border border-gray-200'
                            : isDarkMode
                              ? 'text-gray-400'
                              : 'text-gray-500'
                        }`}
                      >
                        Annuel
                      </button>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Price Display */}
                    <div>
                      <div className="mb-4">
                        <span className={`text-2xl font-bold ${
                          isDarkMode ? 'text-gray-200' : 'text-gray-900'
                        }`}>
                          {isAnnual ? '7,50€' : '9€'}
                        </span>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                          {t.per_month}
                        </span>
                        {isAnnual && (
                          <div className="mt-1 text-sm text-emerald-500 font-medium">
                            Économisez 18€ par an
                          </div>
                        )}
                      </div>
                    </div>

                    {/* What's included section */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <BadgeCheck 
                          size={14} 
                          className="text-[#F98F61]" 
                          strokeWidth={2.5} 
                        />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Suivi du concurrent en temps réel
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BadgeCheck 
                          size={14} 
                          className="text-[#F98F61]" 
                          strokeWidth={2.5} 
                        />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Évolution abonnés (gagnés / perdus)
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BadgeCheck 
                          size={14} 
                          className="text-[#F98F61]" 
                          strokeWidth={2.5} 
                        />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Changements de bio détectés
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <BadgeCheck 
                          size={14} 
                          className="text-[#F98F61]" 
                          strokeWidth={2.5} 
                        />
                        <span className={`text-sm ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-700'
                        }`}>
                          Alertes nouveaux posts
                        </span>
                      </div>
                    </div>

                    {/* Payment Button */}
                    <button
                      onClick={async () => {
                        try {
                          setIsCheckingOut(true);
                          const product = products['Amplr Track Competitor'];
                          if (!product) {
                            console.error('Amplr Track Competitor product not found');
                            return;
                          }
                          
                          const priceId = isAnnual 
                            ? product.EUR.annual.priceId 
                            : product.EUR.monthly.priceId;
                          await createCheckoutSession(priceId, product.mode, 'competitor-tracking', false);
                        } catch (error) {
                          console.error('Error starting competitor tracking checkout:', error);
                          setIsCheckingOut(false);
                        }
                      }}
                      disabled={isCheckingOut}
                      className={`w-full mt-6 rounded-lg py-2 text-[13px] font-medium transition-colors ${
                        isDarkMode
                          ? 'bg-[#2b2b2b] text-gray-300 hover:bg-[#333333]'
                          : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                      } disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2`}
                    >
                      {isCheckingOut ? (
                        <>
                          <Loader2 size={14} className="animate-spin" />
                          <span>Chargement...</span>
                        </>
                      ) : (
                        'Commencer le suivi'
                      )}
                    </button>
                    </div>
                  </div>
              )}
              
              {selectedTab === 'plans' && isFreeSubscription && (
                <div className={`p-6 rounded-lg border ${
                  isDarkMode 
                    ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
                    : 'bg-white border-[#e5e7eb]'
                }`}>
                  <p className={`text-sm leading-relaxed ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Vous bénéficiez actuellement d'une offre personnalisée et exclusive pour le suivi de ce concurrent.
                    <br />
                    Pour toute demande concernant votre abonnement, vous pouvez directement contacter votre Account Manager.
                    <br /><br />
                    Vous avez également la possibilité de nous écrire via le chat en bas à droite du site : notre équipe prendra en charge votre demande sans aucune démarche supplémentaire de votre part.
                    <br /><br />
                    Merci pour votre confiance, nous restons disponibles pour vous accompagner.
                  </p>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center mt-12">
            <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
              Select a competitor to view details
            </p>
          </div>
        )}
      </div>

      {/* Add Competitor Modal */}
      {showAddModal && (
        <AddCompetitorModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddCompetitor}
          isDarkMode={isDarkMode}
          language={language}
        />
      )}
    </div>
  );
}